import React, { useState, useMemo, useEffect } from 'react';
import { Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Line, ComposedChart } from 'recharts';
import { formatCurrencyAmount, numberWithCommas } from '../../../../../utils/miscFunctions';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../../../states/slices/resourceSlice';
import moment from 'moment';
import useBroker from '../../../../../hooks/brokerHook';
import { useQuery } from '@tanstack/react-query';
export interface IMonthlyChartMetrics {
  salesAmounts: number[];
  commissionAmounts: number[];
  revenueTargets: number[];
  months: string[];
}
type SalesTrendsChartProps = {
  data: IMonthlyChartMetrics;
  isLoading?: boolean;
  error?: string | null;
  // Driven by the dashboard's top-level year filter so this chart stays in sync with it.
  year?: string;
  companyId?: number;
}

type MetricType = 'premium' | 'commission';

export const EmptyState: React.FC<{ icon: string; title: string; description: string, classes?: string }> = ({
  icon,
  title,
  description,
  classes
}) => (
  <div className={clsx(`flex flex-col items-center justify-center h-64 p-6 text-center`, classes)}>
    <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
      <span className="text-2xl">{icon}</span>
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-1 whitespace-nowrap dark:text-darkBg-mainText">{title}</h3>
    <p className="text-sm text-gray-500   whitespace-nowrap">{description}</p>
  </div>
);

export const LoadingState: React.FC = () => (
  <div className="flex flex-col items-center justify-center h-64">
    <div className="animate-pulse w-full">
      <div className="h-4 bg-gray-200 rounded w-1/3 mb-4 mx-auto"></div>
      <div className="h-40 bg-gray-100 rounded-md mb-4"></div>
      <div className="flex justify-between px-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-2 w-8 bg-gray-200 rounded"></div>
        ))}
      </div>
    </div>
  </div>
);

const SalesTrendsChart: React.FC<SalesTrendsChartProps> = ({ data, isLoading = false, error = null, year, companyId }) => {
  const [metricType, setMetricType] = useState<MetricType>('premium');
  const [chartDataValues, setChartDataValues] = useState<IMonthlyChartMetrics>(data);
  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry.id || 1;
  const currency = countryId === 1 ? 'GHS' : 'NGN';
  // Single source of truth: the dashboard's top-level year filter. Falls back to the current year.
  const selectedYear = year ?? moment(new Date()).format('YYYY');

  const getCurrentQuarter = () => {
    const currentMonth = new Date().getMonth();
    return Math.floor(currentMonth / 3) + 1;
  }
  const {getDashboardChartMonthlyMetrics, monthlyChartMetrics} = useBroker();

  const { isLoading: isLoadingMonthlyMetrics } = useQuery({
    queryKey: ['getDashboardChartMonthlyMetrics', selectedYear, companyId],
    queryFn: ()=>getDashboardChartMonthlyMetrics(Number(selectedYear), companyId),
    enabled: !!selectedYear,
    // Refetch on mount so the dashboard reflects edits without a full reload,
    // overriding the app-wide 5-min staleTime (src/lib/queryClient.ts).
    refetchOnMount: 'always'
  });

   useEffect(() => {
    if (monthlyChartMetrics) {
      setChartDataValues(monthlyChartMetrics);
    } else {
      setChartDataValues(data);
    }
  }, [monthlyChartMetrics, data]);
  
  // Transform the data for the chart
  const chartData = useMemo(() =>
    chartDataValues.months.map((month, index) => ({
      month,
      premium: chartDataValues.salesAmounts[index] || 0,
      commission: chartDataValues.commissionAmounts[index] || 0,
      target: chartDataValues.revenueTargets[index] || 0
    })),
    [chartDataValues.months, chartDataValues.salesAmounts, chartDataValues.commissionAmounts, chartDataValues.revenueTargets]
  );

  const hasData = chartDataValues.months.length > 0 &&
    (chartDataValues.salesAmounts.some(amount => amount > 0) ||
      chartDataValues.commissionAmounts.some(amount => amount > 0) ||
      chartDataValues.revenueTargets.some(amount => amount > 0));

  if (isLoading || isLoadingMonthlyMetrics) {
    return (
      <div className="bg-white dark:bg-darkBg rounded-2xl shadow-sm border border-gray-200 dark:border-darkBg-border p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{metricType === 'commission' ? 'Commission Performance' : 'Premium Performance'}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Loading data...</p>
          </div>
        </div>
        <LoadingState />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-darkBg rounded-2xl shadow-sm border border-gray-200 dark:border-darkBg-border p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{metricType === 'commission' ? 'Commission Performance' : 'Premium Performance'}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Error loading data</p>
          </div>
        </div>
        <EmptyState
          icon="⚠️"
          title="Error loading data"
          description="We couldn't load the premium performance data. Please try again later."
        />
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="bg-white dark:bg-darkBg rounded-2xl shadow-sm border border-gray-200 dark:border-darkBg-border p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{metricType === 'commission' ? 'Commission Performance' : 'Premium Performance'}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">No data available</p>
          </div>
        </div>
        <EmptyState
          icon="📊"
          title="No premium data available"
          description="There is no premium performance data to display for the selected period."
        />
      </div>
    );
  }

  const getAverageLine = () => {
    const values = chartData.map(item => item[metricType]);
    const sum = values.reduce((sum, val) => sum + val, 0);
    return values.length > 0 ? sum / values.length : 0;
  };

  const average = getAverageLine();
  
  // Calculate quarterly values based on current quarter months only
  const currentQuarter = getCurrentQuarter();
  const currentMonth = new Date().getMonth() + 1; // 1-12
  
  // Get the months that belong to the current quarter
  const getQuarterMonths = (quarter: number) => {
    switch (quarter) {
      case 1: return [1, 2, 3]; // Q1: Jan, Feb, Mar
      case 2: return [4, 5, 6]; // Q2: Apr, May, Jun
      case 3: return [7, 8, 9]; // Q3: Jul, Aug, Sep
      case 4: return [10, 11, 12]; // Q4: Oct, Nov, Dec
      default: return [1, 2, 3];
    }
  };
  
  const quarterMonths = getQuarterMonths(currentQuarter);
  
  // Calculate quarterly values by summing only the months that belong to the current quarter
  
  const quarterlySales = chartDataValues.salesAmounts
    .slice(0, currentMonth)
    .filter((_, index) => quarterMonths.includes(index + 1))
    .reduce((sum, val) => sum + val, 0);
    
  const quarterlyCommission = chartDataValues.commissionAmounts
    .slice(0, currentMonth)
    .filter((_, index) => quarterMonths.includes(index + 1))
    .reduce((sum, val) => sum + val, 0);


  return (
    <div className="bg-white dark:bg-darkBg rounded-2xl h-full shadow-sm border border-gray-200 dark:border-darkBg-border p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{metricType === 'commission' ? 'Commission Performance' : 'Premium Performance'}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Monthly trends and targets</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setMetricType('premium')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg ${metricType === 'premium'
              ? 'bg-indigo-100 text-[#818cf8]'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            Premium
          </button>
          <button
            onClick={() => setMetricType('commission')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg ${metricType === 'commission'
              ? 'bg-indigo-100 text-[#818cf8]'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            Commission
          </button>
        </div>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12 }}
              padding={{ left: 10, right: 10 }}
            />
            <YAxis
              tickFormatter={value => numberWithCommas(value)}
              tick={{ fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              formatter={(value: number, name: string) => {
                // Skip rendering target in tooltip when commission tab is selected
                if (metricType === 'commission' && name === 'Target') {
                  return null;
                }
                
                let label = '';
                if (name === 'Target') {
                  label = 'Target';
                } else if (name === 'Premium Volume') {
                  label = 'Premium Volume';
                } else if (name === 'Commission') {
                  label = 'Commission';
                } else {
                  // Fallback based on current metric type
                  label = metricType === 'premium' ? 'Premium Volume' : 'Commission';
                }
                return [formatCurrencyAmount(value), label];
              }}
              labelStyle={{ fontWeight: 'bold' ,}}
            />
            <Legend 
              wrapperStyle={{ paddingTop: 10 }}
              formatter={(value) => {
                // Hide target label in legend when commission tab is selected
                if (metricType === 'commission' && value === 'Target') {
                  return null;
                }
                return value;
              }}
            />
            <Bar
              dataKey={metricType}
              fill={metricType === 'premium' ? "#818cf8" : "#10b981"}
              name={metricType === 'premium' ? "Premium Volume" : "Commission"}
              radius={[4, 4, 0, 0]}
              barSize={30}
            />
            {metricType === 'premium' && (
              <Line
                type="monotone"
                dataKey="target"
                name="Target"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={{ r: 0 }}
                activeDot={{ r: 6 }}
                label="Target"
              />
            )}
            <ReferenceLine
              y={average}
              stroke="#ef4444"
              strokeDasharray="3 3"
              label={{
                value: `Avg: ${formatCurrencyAmount(average)}`,
                position: 'insideBottomRight',
                fill: '#ef4444',
                fontSize: 12
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-center">
        <div className="p-3 bg-indigo-50 dark:bg-indigo-500 rounded-lg">
          <p className="text-sm font-medium text-gray-600 dark:text-white">Q{currentQuarter} Premium {`(${currency})`}</p>
          <p className="text-lg font-bold text-[#818cf8] dark:text-white">
            {formatCurrencyAmount(quarterlySales,undefined,false)}
          </p>
        </div>
        <div className="p-3 bg-green-50 dark:bg-green-500 rounded-lg">
          <p className="text-sm font-medium text-gray-600 dark:text-white">Q{currentQuarter} Commission {`(${currency})`}</p>
          <p className="text-lg font-bold text-green-700 dark:text-white">
            {formatCurrencyAmount(quarterlyCommission,undefined,false)}
          </p>
        </div>
      </div> */}
    </div>
  );
};

export default SalesTrendsChart;
